import { useCallback, useContext, useEffect, useState } from 'react';
import { map, merge, switchMap, take, takeUntil, tap, throwError } from 'rxjs';

import { SocketContext } from '../../../../providers/SocketProvider/SocketProvider';

interface PromptVariables {
  learningObjective: string;
  grade: string;
  subject?: string;
  languages?: string[];
  lessonCategory?:
    | 'teachSomethingNew'
    | 'reviewOrReteach'
    | 'practiceASkill'
    | 'readAloud'
    | 'closeReading'
    | 'other';
  standardAlignment?: string;
}

export const useLearningObjectiveRefinement = () => {
  const { gameAPI } = useContext(SocketContext);
  const [streamData, setStreamData] = useState<{
    tokens: string[];
    isComplete: boolean;
    currentContent: string;
    state: 'thinking' | 'generating' | undefined;
  }>({
    tokens: [],
    isComplete: false,
    currentContent: '',
    state: undefined,
  });
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isObjectiveReady, setIsObjectiveReady] = useState(false);
  const [promptVariables, setPromptVariables] = useState<PromptVariables | null>(null);
  const [sessionLength, setSessionLength] = useState<number | null>(null);

  const startStream = useCallback(
    (message: string) => {
      console.log('Learning objective stream start attempt:', {
        message: message.substring(0, 100) + '...',
        gameAPI: !!gameAPI,
        isStreaming,
      });

      if (isStreaming) {
        console.warn('Learning objective stream already in progress');
        return;
      }

      // Reset state
      setStreamData({
        tokens: [],
        isComplete: false,
        currentContent: '',
        state: 'thinking',
      });
      setIsStreaming(true);
      setError(null);

      if (!gameAPI) {
        console.error('GameAPI not available for learning objective stream');
        setError(
          new Error(
            'Learning objective refinement is not available in this context. Please use the lesson editor to access this feature.',
          ),
        );
        setIsStreaming(false);
        return;
      }

      console.log('GameAPI is available, proceeding with stream setup');

      // Only take the first connection event, then unsubscribe
      const streamObservable = gameAPI.ensureConnected().pipe(
        take(1),
        tap(() => {
          console.log('GameAPI connected, emitting learning-objective:message');
          // Emit learning objective message event (this is the correct event name)
          gameAPI
            .getSocket()
            .emit('learning-objective:message', { message }, (res: any) => {
              console.log('learning-objective:message response:', res);
              if (res.error) {
                console.error('Learning objective message error:', res.error);
                setError(
                  new Error(
                    res.error.message || 'Failed to send learning objective message',
                  ),
                );
                setIsStreaming(false);
              }
            });
        }),
        switchMap(() => {
          console.log('Setting up learning objective stream observables');

          // Create observables for each event type
          const tokenObservable = gameAPI
            .createEventObservable<string>('learning-objective:stream:onToken')
            .pipe(
              tap(data => console.log('🔗 Received onToken event:', data)),
              map(tokenData => ({ type: 'token' as const, data: tokenData })),
            );

          const streamingStateObservable = gameAPI
            .createEventObservable<{ state: 'thinking' | 'generating' | undefined }>(
              'learning-objective:stream:state',
            )
            .pipe(
              tap(data => console.log('🔗 Received state event:', data)),
              map(stateData => ({ type: 'streamState' as const, data: stateData })),
            );

          // Main event - assistant message added with isObjectiveReady flag
          const assistantMessageAddedObservable = gameAPI
            .createEventObservable<{ content: string; isObjectiveReady?: boolean }>(
              'learning-objective:stream:assistant-message-added',
            )
            .pipe(
              tap(data =>
                console.log('🔗 Received assistant-message-added event:', data),
              ),
              map(messageData => ({
                type: 'assistantMessageAdded' as const,
                data: messageData,
              })),
            );

          const completeObservable = gameAPI
            .createEventObservable<any>('learning-objective:stream:complete')
            .pipe(
              tap(data => {
                console.log('🔗 Received complete event:', data);
                // Check for isObjectiveReady right here in the stream
                if (data.isObjectiveReady === true) {
                  console.log('🎉 Objective is ready! Setting state in observable...');
                  setIsObjectiveReady(true);

                  // Also capture promptVariables if available
                  if (data.promptVariables) {
                    console.log('📋 Received promptVariables:', data.promptVariables);
                    setPromptVariables(data.promptVariables);
                  } else {
                    console.log('⚠️ No promptVariables in complete event');
                  }
                }

                // Capture sessionLength if available
                if (data.sessionLength !== undefined) {
                  console.log('📊 Received sessionLength:', data.sessionLength);
                  setSessionLength(data.sessionLength);
                }
              }),
              map(completeData => ({ type: 'complete' as const, data: completeData })),
            );

          const errorObservable = gameAPI
            .createEventObservable<unknown>('learning-objective:stream:error')
            .pipe(
              tap(data => console.log('🔗 Received error event:', data)),
              map(errorData => ({
                type: 'error' as const,
                data: errorData,
              })),
              switchMap(errorEvent => throwError(() => errorEvent.data)),
            );

          // Merge all observables and complete when the complete event fires
          return merge(
            tokenObservable,
            streamingStateObservable,
            assistantMessageAddedObservable,
            completeObservable,
            errorObservable,
          ).pipe(
            takeUntil(completeObservable.pipe(take(1))), // Complete when complete event fires
          );
        }),
      );

      const subscription = streamObservable.subscribe({
        next: event => {
          console.log('Learning objective stream event:', event);
          console.log('event.type', event.type);

          if (event.type === 'token') {
            console.log('🔗 Processing token event:', event.data);
            setStreamData(prev => ({
              ...prev,
              tokens: [...prev.tokens, event.data],
              currentContent: prev.currentContent + event.data,
            }));
          }

          if (event.type === 'streamState') {
            console.log('🔗 Processing streamState event:', event.data);
            setStreamData(prev => ({
              ...prev,
              state: event.data.state,
            }));
          }

          if (event.type === 'complete') {
            console.log('✅ Processing complete event in subscription:', event.data);
            // Note: isObjectiveReady is already handled in the observable stream above
          }
        },
        error: err => {
          console.error('Learning objective stream error:', err);
          setError(err);
          setIsStreaming(false);
        },
        complete: () => {
          console.log('Learning objective stream completed successfully');
          setIsStreaming(false);
          setStreamData(prev => ({
            ...prev,
            isComplete: true,
          }));
        },
      });

      // Return cleanup function
      return () => {
        console.log('Cleaning up learning objective stream subscription');
        subscription.unsubscribe();
        setIsStreaming(false);
      };
    },
    [gameAPI, isStreaming],
  );

  const resetStream = useCallback(() => {
    setStreamData({
      tokens: [],
      isComplete: false,
      currentContent: '',
      state: undefined,
    });
    setIsStreaming(false);
    setError(null);
  }, []);

  const stopStream = useCallback(() => {
    if (gameAPI) {
      gameAPI.getSocket().emit('learning-objective:stream:stop');
    }
    resetStream();
  }, [gameAPI, resetStream]);

  // Add comprehensive logging for debugging
  useEffect(() => {
    console.log('useLearningObjectiveRefinement Debug Info:', {
      gameAPI: !!gameAPI,
      isStreaming,
      error: error?.message,
    });
  }, [gameAPI, isStreaming, error]);

  return {
    streamData,
    isStreaming,
    error,
    isObjectiveReady,
    promptVariables,
    sessionLength,
    startStream,
    resetStream,
    stopStream,
    setIsObjectiveReady,
  };
};
